import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Text } from '../../../../components/typography';
import styles from './CustomReport.module.scss';
import { loader } from 'graphql.macro';
import { useQuery, useMutation } from '@apollo/client';
import { Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../../utilities/translator/translation_keys';
import TextWithIcon from '../../../../components/typography/TextWithIcon';
import { useNavigate, useParams } from 'react-router-dom';
import SidebarLayout from '../../../../components/layouts/SidebarLayout';
import {
  buildReportOptions,
  exportPng,
  formatPivotDataForExcel,
  GetReportSidebarOptions,
  graphTypes,
  intervalOptions,
  SidebarButtons,
  SidebarSwitches,
} from '../../../../utilities/reports';
import ReportBuildMenu from './ReportBuildMenu';
import { useAttachmentMutations } from '../../../../hooks/attachments/attachment_mutations';
import RichTextEditor from '../../../../components/rich_text/RichTextEditor';
import Icon from '../../../../components/Icon';
import { customColors } from '../../../../utilities';
import ReportBuildMenuGraphs from './ReportBuildMenuGraphs';
import { useModal } from '../../../../providers/ModalProvider';
import { useFileUpload } from '../../../../hooks/offline-hooks/addFile';
import Label from '../../../../components/action_items/Label';
import { downloadPDF } from '../../../../utilities/pdf_export/misc';
import { downloadFile } from '../../../../utilities/inspection';
import ReportBuildMenuData from './ReportBuildMenuData';
import Spacer from '../../../../components/typography/Spacer';
import { Button } from '../../../../components';
import { useLabels } from '../../../../providers/LabelProvider';
import { useWorkspace } from '../../../../providers/WorkspaceProvider';
import { useWorkspacePermissions } from '../../../../providers/WorkspacePermissionsProvider';
import IncidentHistoricalStatGraph from '../IncidentHistoricalStatGraph';
import { useCurrentUser } from '../../../../providers/UserProvider';
import {
  exportToExcel,
  exportWorkbook,
} from '../../../../components/excel/ExcelExport';
import { excelHeaderLookup } from '../../../../utilities/excel_export/data_converter';
import { buildReportExcelSheets } from './custom_reports';
import { format } from 'date-fns';
import { useTemplates } from '../../../../providers/TemplateProvider';
import ReportBuildMenuStats from './ReportBuildMenuStats';
import { getLocalTime, getCalendarDate } from '../../../../utilities/time';
import DeletedReportPage from '../../../DeletedReportPage';

const getReportQuery = loader('./CustomReports.get.graphql');
const updateReportMutation = loader('./CustomReports.update.graphql');
const deleteReportMutation = loader('./CustomReports.delete.graphql');

const takeSnapShot = graphTypes.flatMap((g) => g.key);

function getTomorrowYMD() {
  const today = new Date();
  const tomorrowDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + 1,
  );
  return `${tomorrowDate.getFullYear()}-${String(
    tomorrowDate.getMonth() + 1,
  ).padStart(2, '0')}-${String(tomorrowDate.getDate()).padStart(2, '0')}`;
}

function getDefaultState() {
  const today = new Date();
  const end = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - 1,
    23,
    59,
    59,
    999,
  );
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - 29,
    0,
    0,
    0,
    0,
  );

  return {
    daysBack: '30',
    key: 'dateCreated',
    model: 'actionItem',
    refModel: 'actionItem',
    timeRange: { from: start, to: end },
    workspaces: [],
    type: 'TEXT',
    group: 'workspaceId',
    subgroup: null,
    filters: [],
    where: {},
    description: '',
    total: 0,
    title: null,
    columns: null,
    value: 'actionItems',
    template: null,
    questions: [],
  };
}

export default function CustomReport({ refetchList = () => {} }) {
  const { models } = useLabels();
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useCurrentUser();
  const reportId = id ? parseInt(id) : false;
  const navigate = useNavigate();
  const { addAttachment, deleteAttachment, updateAttachment } =
    useAttachmentMutations();
  const [menuState, setMenuState] = useState(getDefaultState);
  const [menuOpen, setMenuOpen] = useState(false);
  const [updateReport] = useMutation(updateReportMutation);
  const [deleteReport] = useMutation(deleteReportMutation);
  const attachmentRefs = useRef({});
  const [state, setState] = useState({
    description: '',
    title: '',
    interval: 'WEEK',
    startDate: getTomorrowYMD(),
  });
  const [editingAttachment, setEditingAttachment] = useState(null);
  const editablePreviewRef = useRef(null);
  const scrollRef = useRef(null);
  const editingRef = useRef(null);
  const graphRefs = useRef({});
  const [menuType, setMenuType] = useState('TEXT');
  const { openConfirmationModal } = useModal();
  const [loading, setLoading] = useState(false);
  const [savingAttachment, setSavingAttachment] = useState(false);
  const [tablePDF, setTablePDF] = useState({});
  const [graphPDF, setGraphPDF] = useState({});
  const [statsPDF, setStatsPDF] = useState({});
  const [tableExport, setTableExport] = useState({});
  const { allVehicles, allWorkspaces } = useWorkspace();
  const { allTemplates } = useTemplates();
  const { allUsers } = useWorkspacePermissions();
  const { labels: allLabels } = useLabels();
  const { company } = useCurrentUser();
  const { addFileToS3 } = useFileUpload();

  useEffect(() => {
    if (!menuOpen) {
      setMenuState(getDefaultState());
      setMenuType('TEXT');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);

  useEffect(() => {
    const shouldFocusEditable =
      menuOpen && !editingAttachment && menuType !== 'TEXT';
    if (!shouldFocusEditable || !scrollRef?.current) return;
    const id = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus?.();
    });
    return () => cancelAnimationFrame(id);
  }, [menuOpen, editingAttachment, menuType]);

  const {
    refetch: refetchReport,
    loading: queryLoading,
    data: { reports: [report] = [{}] } = {},
  } = useQuery(getReportQuery, {
    skip: !reportId,
    variables: {
      id: `${reportId}`,
    },
    onCompleted: (data) => {
      if (!!data?.reports?.length) {
        const r = data.reports[0];
        setState({
          ...r,
          interval: r.interval ?? 'WEEK',
          startDate: r.startDate ?? getTomorrowYMD(),
        });
      }
    },
  });

  const readOnly = useMemo(() => report?.status !== 'DRAFT', [report?.status]);
  const attachments = useMemo(() => report?.attachments, [report?.attachments]);
  const prevReportId = report?.prevReportId ?? null;
  const nextReportId = report?.nextReportId ?? null;

  const showEditing =
    !readOnly &&
    editingAttachment === null &&
    menuType !== 'TEXT' &&
    !!menuOpen;

  function onEdit() {
    const version = report.version + 1;
    updateReport({ variables: { reportId, status: 'DRAFT', version } });
  }

  function onPublish() {
    setMenuState({ ...menuState, status: 'PUBLISHED' });
    updateReport({
      variables: {
        reportId,
        status: 'PUBLISHED',
        startDate: state.startDate ?? getTomorrowYMD(),
        interval: state.interval ?? undefined,
      },
    }).then(() => refetchReport());
  }

  async function onPDF() {
    const fileName = `${report?.title}-${format(new Date(), 'yyyy-MM-dd')}`;
    await downloadPDF({
      fileName,
      customReport: { ...report, models },
      props: { tablePDF, graphPDF, statsPDF },
      lookups,
      company,
      timeSetting: user?.timeSetting,
    });
  }
  function onExcelExport() {
    const sheets = buildReportExcelSheets(
      report,
      models,
      { graphPDF, tableExport },
      lookups,
    );
    exportWorkbook({
      sheets,
      fileName: `${report?.title}-${format(new Date(), 'yyyy-MM-dd')}`,
      exportedBy: `${user.firstName} ${user.lastName}`,
      reportType: ['Report', report?.title || 'Custom Report'],
    });
  }
  function onDelete() {
    openConfirmationModal({
      title: 'Delete Report',
      description: `Are you sure you want to delete this ${
        report.status
      } Report? ${
        report.status === 'DRAFT'
          ? 'This action cannot be undone.'
          : 'This will stop all scheduled reports.'
      }`,
      variant: 'danger',
      onSubmit: () =>
        deleteReport({ variables: { reportId } }).then(() =>
          navigate('/reports'),
        ),
    });
  }
  function onPause(status) {
    setState((prev) => ({ ...prev, status }));
    updateReport({ variables: { reportId, status } });
  }

  function onAdd() {
    if (!menuOpen) {
      setMenuOpen(true);
    }
  }

  const disabled = useMemo(() => !attachments?.length, [attachments?.length]);

  const sidebarOptions = GetReportSidebarOptions(
    state,
    setState,
    readOnly,
    updateReport,
  );
  const sidebarButtons = SidebarButtons(
    state,
    onEdit,
    onDelete,
    onPublish,
    onAdd,
    onPDF,
    disabled,
    onPause,
    onExcelExport,
  );
  const sidebarSwitches = SidebarSwitches(state, async (e, field) =>
    updateReport({ variables: { reportId, [field]: e.target.checked } }),
  );
  const model = models[menuState.model] || 'actionItem';
  const lookups = useMemo(
    () => ({
      allVehicles,
      allWorkspaces,
      allUsers,
      allLabels,
      allTemplates,
    }),
    [allLabels, allVehicles, allUsers, allWorkspaces, allTemplates],
  );

  const updatePDF = useCallback((id, data) => {
    setGraphPDF((prev) => ({ ...prev, [id]: data }));
  }, []);

  return !loading && !queryLoading && !report?.id ? (
    <DeletedReportPage />
  ) : (
    <div className={styles.container}>
      <div className={styles.left}>
        <div className={styles.header}>
          <div>
            <div className={styles.flexRow}>
              <TextWithIcon size="lg" noMargin weight="bold">
                Analytic Report
              </TextWithIcon>
              <Label rowsized={true} name="BETA" color="blue" />
            </div>
          </div>
          <TextWithIcon
            icon="arrow_right"
            onClick={() => {
              refetchList();
              navigate('/reports');
            }}
            color="accentPrimary"
            weight="semiBold"
            hover
          >
            All Reports
          </TextWithIcon>
        </div>
        {report.status === 'PAUSED' ? (
          <>
            <Spacer />
            <TextWithIcon
              leftIcon="error_outlined"
              noMargin
              color="yellow"
              weight="semiBold"
              textAlign="center"
            >
              Paused reports will still be generated, but no emails or
              notifications will be sent.
            </TextWithIcon>
          </>
        ) : null}
        <div className={styles.page}>
          <div className={styles.pageHeader}>
            {!readOnly ? (
              <div>
                <div className={styles.header}>
                  <Text weight="semiBold" size="sm" color="secondary" noMargin>
                    {t(keys.common.TITLE)}
                  </Text>
                </div>
                <div className={styles.flexRow}>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    className={styles.formInput}
                    placeholder={t(keys.report.ENTER_REPORT_TITLE)}
                    value={state?.title || ''}
                    onChange={({ target: { value } }) => {
                      setState({ ...state, title: value });
                    }}
                    onBlur={async (e) => {
                      const newTitle = e.target.value;
                      if (newTitle === report?.title) {
                        updateReport({
                          variables: { reportId, title: newTitle },
                        });
                        return;
                      }
                      updateReport({
                        variables: {
                          reportId,
                          title: newTitle,
                        },
                      });
                    }}
                  />
                </div>
                <Spacer />
                <div>
                  <Text weight="semiBold" size="sm" color="secondary" noMargin>
                    {t(keys.common.DESCRIPTION)}
                  </Text>
                  <div className={styles.flexRow}>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      className={styles.formInput}
                      placeholder={t(keys.action.ENTER, {
                        variable: t(keys.common.DESCRIPTION),
                      })}
                      value={state?.description || ''}
                      onChange={({ target: { value } }) => {
                        setState({ ...state, description: value });
                      }}
                      onBlur={(e) =>
                        updateReport({
                          variables: { reportId, description: e.target.value },
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.title}>
                <div className={styles.header}>
                  <Text
                    size="sm"
                    weight="semiBold"
                    color="secondary"
                    noMargin
                  >{`${intervalOptions[report.interval]?.label} - V${
                    report.version
                  }`}</Text>
                  <TextWithIcon
                    color="secondary"
                    weight="semiBold"
                    noMargin
                    size="sm"
                    leftIcon={
                      getLocalTime(state.nextRunAt).isAfter(getLocalTime())
                        ? 'schedule'
                        : undefined
                    }
                    iconProps={{ fontSize: '1rem' }}
                  >
                    {state?.status === 'COMPLETE'
                      ? getCalendarDate(state.dateCreated).format(
                          'dddd MMMM Do, YYYY',
                        )
                      : getCalendarDate(state.nextRunAt).format(
                          'dddd MMMM Do, YYYY',
                        )}
                  </TextWithIcon>
                </div>
                <Spacer size="xs" />
                <Text weight="bold" noMargin size="lg">
                  {state?.title}
                </Text>
                {state?.description ? (
                  <Text noMargin>{state?.description}</Text>
                ) : null}
              </div>
            )}
          </div>
          {report?.status === 'DRAFT' ? (
            <Text
              weight="semiBold"
              color="secondary"
              size="sm"
              noMargin
              textAlign="center"
            >
              **Note: This report shows sample data from today. The delivered
              report will reflect the selected date range.
            </Text>
          ) : null}
          <div>
            {attachments?.length || savingAttachment ? (
              <div className={styles.attachments}>
                {[...(attachments || [])]
                  ?.sort((a, b) => a?.sortIndex - b?.sortIndex)
                  ?.map((a) => {
                    const isEditing =
                      editingAttachment?.id === a.id && !readOnly;
                    const type = isEditing
                      ? menuType || menuState?.type
                      : a.type;
                    const isText = type === 'TEXT';
                    const isData = type === 'DATA';
                    const isPivot = type === 'PIVOT';
                    const isStats = type === 'STATS';
                    const options = isText ? null : JSON.parse(a.content);
                    const isChart = !isText && !isData && !isPivot;
                    const rows = tableExport?.[a.id] || [];

                    return (
                      <div
                        tabIndex={-1}
                        className={[
                          styles.attachmentBorder,
                          isEditing ? styles.selectedAttachment : '',
                        ].join(' ')}
                        key={a.id}
                      >
                        {!isEditing && (!readOnly || (!isText && !isStats)) && (
                          <div className={styles.editIcons}>
                            {!isText && !isStats && (
                              <Icon
                                disabled={isData && rows?.length < 1}
                                hover
                                style={{
                                  color: customColors.BLUE,
                                  fontSize: '1.2rem',
                                }}
                                onClick={async (e) => {
                                  e.preventDefault();
                                  if (isText || (isData && rows?.length < 1))
                                    return;
                                  const { content } = a || {};
                                  const f = JSON.parse(content);
                                  const fileName = `${String(
                                    f?.model || f?.type || a.type,
                                  )}-${String(
                                    f.daysBack ? `${f.daysBack}days` : '',
                                  )} ${String(
                                    f.timeRange.from?.split('T')?.[0],
                                  )}-${String(
                                    f.timeRange.to?.split('T')?.[0],
                                  )}`;
                                  if (isChart) {
                                    if (a.url) {
                                      downloadFile(
                                        a.url,
                                        fileName || 'Report_Graph_Image',
                                      );
                                    } else {
                                      setLoading(true);
                                      try {
                                        const file = await exportPng({
                                          ...f,
                                          ref: {
                                            current: graphRefs.current[a.id],
                                          },
                                        });
                                        await addFileToS3({
                                          file,
                                          setLoading,
                                          onSubmit: async ({
                                            url,
                                            name,
                                            extension,
                                            size,
                                          }) => {
                                            await updateAttachment({
                                              id: a.id,
                                              content: a.content,
                                              type: a.type,
                                              url,
                                              name,
                                              extension,
                                              size,
                                            });
                                            await refetchReport();
                                            downloadFile(
                                              url,
                                              fileName || 'Report_Graph_Image',
                                            );
                                          },
                                        });
                                      } finally {
                                        setLoading(false);
                                      }
                                    }
                                  } else if (isData) {
                                    if (!!rows) {
                                      exportToExcel({
                                        data: rows,
                                        exportedBy: `${user.firstName} ${user.lastName}`,
                                        sheetName: `${
                                          f?.model || f?.type || a.type
                                        }`,
                                        headerMap: excelHeaderLookup,
                                        fileName: `${fileName}_${format(
                                          new Date(),
                                          'yyyy-MM-dd',
                                        )}`,
                                        reportType: [
                                          `Report`,
                                          `${report.title}` || 'Custom Report',
                                        ],
                                      });
                                    }
                                  } else if (isPivot) {
                                    if (!!rows) {
                                      const pivotData = graphPDF[a.id];
                                      const { x, y } = pivotData ?? {};

                                      if (x?.length > 0 && y?.length > 0) {
                                        const formattedForExcel =
                                          formatPivotDataForExcel(
                                            pivotData,
                                            undefined,
                                            t,
                                          );

                                        exportToExcel({
                                          data: formattedForExcel,
                                          exportedBy: `${user.firstName} ${user.lastName}`,
                                          sheetName: `${
                                            f?.model || f?.type || a.type
                                          }`,
                                          headerMap: excelHeaderLookup,
                                          fileName: `${fileName}_${format(
                                            new Date(),
                                            'yyyy-MM-dd',
                                          )}`,
                                          reportType: [
                                            `Report`,
                                            `${report.title}` ||
                                              'Custom Report',
                                          ],
                                        });
                                      }
                                    }
                                  }
                                }}
                              >
                                file_upload
                              </Icon>
                            )}
                            {!readOnly ? (
                              <>
                                <Icon
                                  hover
                                  style={{
                                    fontSize: '1.2rem',
                                    color: customColors.SECONDARY,
                                  }}
                                  onClick={() => {
                                    if (!readOnly) {
                                      setEditingAttachment(a);
                                      setMenuType(a.type);
                                      if (!isText) {
                                        const spec = JSON.parse(a.content);
                                        setMenuState(spec);
                                      }
                                      setMenuOpen(true);
                                    }
                                  }}
                                >
                                  edit
                                </Icon>
                                <Icon
                                  hover
                                  style={{
                                    fontSize: '1.2rem',
                                    color: customColors.RED,
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    openConfirmationModal({
                                      title: 'Delete Section',
                                      description:
                                        'Are you sure you want to delete this section? This action cannot be undone.',
                                      variant: 'warning',
                                      onSubmit: () => {
                                        deleteAttachment({ id: a.id }).then(
                                          () => refetchReport(),
                                        );
                                      },
                                    });
                                  }}
                                >
                                  delete
                                </Icon>
                              </>
                            ) : null}
                          </div>
                        )}
                        <div
                          className={
                            isEditing
                              ? styles.editableChartContainer
                              : styles.graphContainer
                          }
                        >
                          {isData ? (
                            <ReportBuildMenuData
                              options={isEditing ? menuState : options}
                              workspaces={menuState.workspaces}
                              model={!isEditing ? models[options.model] : model}
                              readOnly={readOnly}
                              isEditing={isEditing}
                              setTablePDF={setTablePDF}
                              tablePDF={tablePDF}
                              attachmentId={a.id}
                              setTableExport={setTableExport}
                              tableExport={tableExport}
                            />
                          ) : isText ? (
                            <div key={a.id} className={styles.textSection}>
                              <RichTextEditor
                                readOnly={true}
                                value={a.content}
                                className={styles.section}
                              />
                            </div>
                          ) : isStats ? (
                            <ReportBuildMenuStats
                              statsPDF={statsPDF}
                              setStatsPDF={setStatsPDF}
                              template={
                                isEditing ? menuState.template : a.template
                              }
                              options={isEditing ? menuState : options}
                              setMenuState={setMenuState}
                              attachmentId={a.id}
                            />
                          ) : (
                            <div
                              ref={(node) => {
                                editingRef.current = node;
                                if (node) graphRefs.current[a.id] = node;
                              }}
                              className={
                                !isEditing
                                  ? styles.chartContainer
                                  : styles.editableChartContainer
                              }
                              key={a.id}
                            >
                              <ReportBuildMenuGraphs
                                type={isEditing ? menuState.type : a.type}
                                options={isEditing ? menuState : options}
                                description={a.description || null}
                                attachmentId={a.id}
                                updatePDF={updatePDF}
                              />
                            </div>
                          )}
                        </div>
                        <div
                          ref={(node) => {
                            if (node) attachmentRefs.current[a.id] = node;
                          }}
                        />
                      </div>
                    );
                  })
                  ?.sort((a, b) => a.sortIndex - b.sortIndex)}
                {savingAttachment && (
                  <div className={styles.attachmentBorder}>
                    <div className={styles.savingPlaceholder}>
                      <Spinner animation="border" size="sm" />
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
          {showEditing ? (
            <div
              tabIndex={-1}
              className={styles.editableChartContainer}
              ref={editablePreviewRef}
            >
              {menuType === 'STATS' ? (
                <ReportBuildMenuStats
                  template={menuState.template}
                  options={menuState}
                  setMenuState={setMenuState}
                />
              ) : menuState.type === 'DATA' ? (
                <ReportBuildMenuData
                  options={menuState}
                  workspaces={menuState.workspaces}
                  model={model}
                />
              ) : menuState.group === 'TRIF' ? (
                <IncidentHistoricalStatGraph readOnly={true} />
              ) : (
                <ReportBuildMenuGraphs
                  loading={loading}
                  type={menuState.type}
                  options={buildReportOptions(menuState)}
                />
              )}
            </div>
          ) : null}

          {!menuOpen &&
            !readOnly &&
            !attachments?.length &&
            !savingAttachment && (
              <div>
                <br />
                <Text
                  weight="semiBold"
                  color="accentPrimary"
                  textAlign="center"
                  noMargin
                  hover
                  size="md-lg"
                  onClick={() => {
                    setMenuOpen(true);
                  }}
                >
                  Add Content +
                </Text>
              </div>
            )}
          <ReportBuildMenu
            attachment={editingAttachment}
            allAttachments={attachments}
            open={menuOpen}
            menuState={menuState}
            menuType={menuType}
            setMenuType={setMenuType}
            moveAttachment={({ id, sortIndex }) =>
              updateAttachment({ id, sortIndex }).then(() =>
                refetchReport().then(() =>
                  setEditingAttachment({ ...editingAttachment, sortIndex }),
                ),
              )
            }
            onClose={() => {
              setMenuOpen(false);
              setMenuState(getDefaultState());
              setEditingAttachment(null);
            }}
            onEdit={async ({ id, content, type, description }) => {
              setLoading(true);
              try {
                if (type === 'TEXT') {
                  await updateAttachment({
                    id,
                    content,
                    type,
                    version: report.version || 1,
                  }).then(() => refetchReport());
                  await refetchReport();
                } else {
                  let file = {};
                  if (takeSnapShot.includes(type) && type !== 'PIVOT') {
                    file = await exportPng({
                      ...menuState,
                      ref: editingRef,
                    });
                    await addFileToS3({
                      setLoading,
                      file,
                      onSubmit: async ({ url, name, extension, size }) => {
                        await updateAttachment({
                          id,
                          content,
                          type,
                          description,
                          url,
                          name,
                          extension,
                          size,
                        }).then(() => refetchReport());
                      },
                    });
                  } else {
                    await updateAttachment({
                      id,
                      content,
                      type,
                      description,
                    }).then(() => refetchReport());
                  }
                }
                setMenuState(getDefaultState());
              } finally {
                refetchReport();
                setLoading(false);
              }
            }}
            onSubmit={async ({ content, type }) => {
              setLoading(true);
              setSavingAttachment(true);
              try {
                if (type === 'TEXT') {
                  await addAttachment({
                    content: String(content),
                    parentId: reportId,
                    model: 'report',
                    type,
                    version: report.version || 1,
                  });
                  await refetchReport();
                } else {
                  let file = {};
                  if (takeSnapShot.includes(type) && type !== 'PIVOT') {
                    file = await exportPng({
                      ...menuState,
                      ref: editablePreviewRef,
                    });
                    await addFileToS3({
                      file,
                      onSubmit: async ({ url, name, extension, size }) => {
                        await addAttachment({
                          url,
                          name,
                          extension,
                          content: String(content),
                          parentId: reportId,
                          model: 'report',
                          type,
                          description: menuState.description,
                          size,
                          version: report.version || 1,
                        }).then(() => refetchReport());
                      },
                    });
                  } else {
                    await addAttachment({
                      content: String(content),
                      parentId: reportId,
                      model: 'report',
                      type,
                      description: menuState.description,
                      version: report.version || 1,
                    }).then(() => refetchReport());
                  }

                  await refetchReport();
                }
                setMenuState(getDefaultState());
              } finally {
                setSavingAttachment(false);
                setLoading(false);
              }
            }}
            setMenuState={setMenuState}
          />
        </div>
        <br />
        <br />
        <br />
        <div ref={scrollRef} />
      </div>

      <div className={styles.right}>
        <SidebarLayout
          topComponent={
            <Text noMargin weight="semiBold">
              {`Version ${report.version}`}
            </Text>
          }
          options={sidebarOptions}
          buttons={sidebarButtons}
          switches={sidebarSwitches}
          readOnly={readOnly}
          navigationComponent={
            <div className={styles.navigationButtons}>
              <Button
                disabled={!prevReportId}
                outlined
                size="xs"
                icon="arrow_left"
                onClick={() => navigate(`/report/${prevReportId}`)}
                weight="semiBold"
                hover
                value={t(keys.action.BACK)}
              />
              <Button
                disabled={!nextReportId}
                outlined
                size="xs"
                icon="arrow_right"
                onClick={() => navigate(`/report/${nextReportId}`)}
                weight="semiBold"
                hover
                value={t(keys.action.NEXT)}
              />
            </div>
          }
        />
      </div>
    </div>
  );
}
